/**
 * JSON-safe domain model shared by web, the authoritative server, the bot,
 * the coach and future Android clients. Nothing here references a platform API.
 */

import type { Tile, TileKind } from "./tiles.js";

export type PlayerId = 0 | 1 | 2 | 3;

export const PLAYER_IDS: readonly PlayerId[] = [0, 1, 2, 3];

/** Seat winds in turn order (counter-clockwise in a physical game). */
export type SeatWind = "east" | "south" | "west" | "north";

export const SEAT_WINDS: readonly SeatWind[] = ["east", "south", "west", "north"];

/** A group inside a *winning-hand analysis*: kinds only, no physical tiles. */
export type GroupKind = "chow" | "pong" | "kong" | "pair";

export interface HandGroup {
  kind: GroupKind;
  tiles: TileKind[];
}

export type MeldKind = "chow" | "pong" | "kong";

export type KongKind = "claimed" | "concealed" | "added";

/** A meld the player actually holds: physical tiles, usually exposed. */
export interface Meld {
  kind: MeldKind;
  tiles: Tile[];
  /** Concealed kongs stay concealed; claimed melds never are. */
  concealed: boolean;
  /** Who discarded the claimed tile, or null for concealed melds. */
  claimedFrom: PlayerId | null;
  /** Only set for kongs. */
  kongKind?: KongKind;
}

export type GamePhase = "awaiting-draw" | "awaiting-discard" | "awaiting-claims" | "ended";

export interface PlayerState {
  id: PlayerId;
  seat: SeatWind;
  /** Concealed tiles, always kept sorted. */
  hand: Tile[];
  /** The tile just drawn, kept apart from `hand` so UIs can highlight it. */
  drawnTile: Tile | null;
  /** Discard pile in discard order (public information). */
  discards: Tile[];
  /** Exposed (and concealed kong) melds. */
  melds: Meld[];
}

export interface DiscardRef {
  playerId: PlayerId;
  tile: Tile;
}

export type ClaimType = "chow" | "pong" | "kong" | "ron";

export interface DeclaredClaim {
  playerId: PlayerId;
  type: ClaimType;
  /** Tiles from the claimer's own concealed hand used for the meld. */
  tileIds: number[];
}

/** The interruption window opened by a discard. */
export interface PendingClaims {
  discard: DiscardRef;
  /** Players who have a legal claim and have not answered yet. */
  waitingOn: PlayerId[];
  /** Claims declared so far; passes are not recorded. */
  declared: DeclaredClaim[];
}

export type WinBy = "self-draw" | "discard";

export interface WinningHand {
  /** The four sets, in canonical order. The pair is reported separately. */
  melds: HandGroup[];
  pair: TileKind;
}

export type GameResult =
  | {
      type: "win";
      winner: PlayerId;
      by: WinBy;
      /** Who discarded the winning tile; null for a self-draw. */
      from: PlayerId | null;
      winningTile: TileKind;
      hand: WinningHand;
    }
  | { type: "exhaustive-draw" };

export interface GameState {
  /** Bumped when the serialized shape changes. */
  version: 2;
  ruleSetId: string;
  seed: string;
  dealer: PlayerId;
  turn: PlayerId;
  phase: GamePhase;
  /** Live wall; tiles are drawn from the front. */
  wall: Tile[];
  /** Replacement tiles for kongs. Empty when the rule set has no dead wall. */
  deadWall: Tile[];
  players: [PlayerState, PlayerState, PlayerState, PlayerState];
  lastDiscard: DiscardRef | null;
  pendingClaims: PendingClaims | null;
  /** Number of actions successfully applied since the deal. */
  actionCount: number;
  result: GameResult | null;
}

export interface DrawAction {
  type: "draw";
  playerId: PlayerId;
}

export interface DiscardAction {
  type: "discard";
  playerId: PlayerId;
  tileId: number;
}

export interface DeclareWinAction {
  type: "declare-win";
  playerId: PlayerId;
}

/** Claim a discarded tile, or (for kong) declare one from your own hand. */
export interface ClaimAction {
  type: ClaimType;
  playerId: PlayerId;
  /** Tiles from the claimer's concealed hand that complete the meld. */
  tileIds: number[];
}

/** Decline the open claim window. */
export interface PassAction {
  type: "pass";
  playerId: PlayerId;
}

export type Action =
  | DrawAction
  | DiscardAction
  | DeclareWinAction
  | ClaimAction
  | PassAction;

export type ActionErrorCode =
  | "GAME_ENDED"
  | "UNKNOWN_PLAYER"
  | "NOT_YOUR_TURN"
  | "WRONG_PHASE"
  | "WALL_EMPTY"
  | "TILE_NOT_IN_HAND"
  | "NOT_A_WINNING_HAND"
  | "CLAIM_NOT_SUPPORTED"
  | "NOT_IN_CLAIM_WINDOW"
  | "ILLEGAL_CLAIM"
  | "UNKNOWN_ACTION";

export interface ActionError {
  code: ActionErrorCode;
  message: string;
}

export type GameEvent =
  | { type: "drew"; playerId: PlayerId; tile: Tile; wallRemaining: number; replacement: boolean }
  | { type: "discarded"; playerId: PlayerId; tile: Tile; fromHand: boolean }
  | { type: "claim-window-opened"; discard: DiscardRef; waitingOn: PlayerId[] }
  | { type: "claim-declared"; playerId: PlayerId; claim: ClaimType }
  | { type: "passed"; playerId: PlayerId }
  | {
      type: "meld-formed";
      playerId: PlayerId;
      meld: Meld;
      /** Null for concealed / added kongs declared from the player's own hand. */
      claimedTile: Tile | null;
    }
  | { type: "turn-changed"; playerId: PlayerId }
  | {
      type: "won";
      playerId: PlayerId;
      by: WinBy;
      from: PlayerId | null;
      winningTile: TileKind;
      hand: WinningHand;
    }
  | { type: "exhaustive-draw" };

export type ApplyResult =
  | { ok: true; state: GameState; events: GameEvent[] }
  | { ok: false; error: ActionError };
